import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Phone } from 'lucide-react';
import PhoneLogin from '../components/PhoneLogin';
import Logo from '../components/Logo';
import ScrollReveal from '../components/ui/ScrollReveal';

const Login = () => {
  const [loginMethod, setLoginMethod] = useState('email');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, loginWithGoogle, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleGoogleComplete = (event) => {
      const googleUser = event.detail;
      if (googleUser) {
        if (googleUser.role === 'caregiver') navigate('/caregiver-profile', { replace: true });
        else if (googleUser.role === 'admin') navigate('/admin-dashboard', { replace: true });
        else navigate('/owner-profile', { replace: true });
      }
    };
    window.addEventListener('google-signin-complete', handleGoogleComplete);
    return () => window.removeEventListener('google-signin-complete', handleGoogleComplete);
  }, [navigate]);

  useEffect(() => {
    if (user && !authLoading) {
      if (user.role === 'caregiver') navigate('/caregiver-profile', { replace: true });
      else if (user.role === 'admin') navigate('/admin-dashboard', { replace: true });
      else navigate('/owner-profile', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const loggedIn = await login(formData.email, formData.password);
      if (loggedIn.role === 'caregiver') navigate('/caregiver-profile', { replace: true });
      else if (loggedIn.role === 'admin') navigate('/admin-dashboard', { replace: true });
      else navigate('/owner-profile', { replace: true });
    } catch (err) {
      if (err.message?.includes('user-not-found')) setError('No account found with this email. Please sign up first.');
      else if (err.message?.includes('wrong-password')) setError('Incorrect password. Please try again.');
      else if (err.message?.includes('invalid-email')) setError('Invalid email format.');
      else setError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const result = await loginWithGoogle();
      if (result.pending) return;
      if (result.success && result.user) {
        const u = result.user;
        if (u.role === 'caregiver') navigate('/caregiver-profile', { replace: true });
        else if (u.role === 'admin') navigate('/admin-dashboard', { replace: true });
        else navigate('/owner-profile', { replace: true });
      } else if (result.error) {
        setError(result.error);
        setGoogleLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="pf-auth-panel">
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[var(--pf-canvas)]">
        <ScrollReveal direction="left" className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h2 className="pf-display text-3xl text-[var(--pf-ink)]">Welcome back</h2>
            <p className="mt-2 text-[var(--pf-ink-muted)]">
              Sign in to access your dashboard, bookings, and messages
            </p>
          </div>

          <div className="pf-organic-card p-6 space-y-5">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading || loading}
              className="pf-btn-secondary w-full justify-center"
            >
              {googleLoading ? 'Signing in…' : 'Continue with Google'}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[var(--pf-border)]" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-2 bg-[var(--pf-surface)] text-[var(--pf-ink-muted)]">or</span></div>
            </div>

            <div className="grid grid-cols-2 gap-2 p-1 bg-[var(--pf-mint)] rounded-xl">
              {[
                { id: 'email', label: 'Email', icon: Mail },
                { id: 'phone', label: 'Phone', icon: Phone },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setLoginMethod(id)}
                  className={`py-2.5 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    loginMethod === id
                      ? 'bg-[var(--pf-surface)] text-[var(--pf-accent)] shadow-sm'
                      : 'text-[var(--pf-ink-muted)]'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>

            {loginMethod === 'email' && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
                )}

                <div className="pf-form-group">
                  <label htmlFor="email" className="pf-label">Email or username</label>
                  <div className="input-icon-wrap">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
                    <input id="email" name="email" type="text" required value={formData.email} onChange={handleChange} className="pf-input" placeholder="you@email.com" />
                  </div>
                </div>

                <div className="pf-form-group">
                  <label htmlFor="password" className="pf-label">Password</label>
                  <div className="input-icon-wrap relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
                    <input id="password" name="password" type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={handleChange} className="pf-input" placeholder="Your password" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)]" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="pf-btn-primary w-full justify-center">
                  {loading ? 'Signing in…' : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>
            )}

            {loginMethod === 'phone' && <PhoneLogin onBack={() => setLoginMethod('email')} />}
          </div>

          {loginMethod === 'email' && (
            <p className="text-center text-sm text-[var(--pf-ink-muted)]">
              No account yet?{' '}
              <Link to="/signup" className="font-semibold text-[var(--pf-accent)] hover:underline">Create one free</Link>
            </p>
          )}
        </ScrollReveal>
      </div>

      <ScrollReveal direction="right" className="pf-auth-visual">
        <img src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="pf-hero-v2__pattern absolute inset-0" />
        <div className="relative z-10">
          <Logo to="/" size={34} variant="light" className="mb-6" />
          <h3 className="pf-display text-3xl text-white mb-3">Trusted pet care, for everyone</h3>
          <p className="text-blue-100/90 text-lg leading-relaxed max-w-md">
            Pet parents book care. Caregivers manage clients. Everything in one place.
          </p>
        </div>
      </ScrollReveal>
    </div>
  );
};

export default Login;
